import { useState, useEffect, useCallback } from 'react';
import cloudApi from '../services/cloudApi';
import {
  getActiveAnnouncements,
  dismissAnnouncement,
  anyAnnouncementNeedsTaskCount,
} from './announcements';

/**
 * Resolves the announcements that should currently be shown to the user.
 *
 * Fetches the user's task count lazily — only when at least one live
 * announcement targets `minTasks` — so the common case (no announcements, or
 * none needing a count) does zero extra work.
 *
 * @param {{ enabled?: boolean, plan?: string|null, view?: string }} options
 * @returns {{ announcements: Array, dismiss: (id: string) => void }}
 */
export function useAnnouncements({ enabled = true, plan = null, view } = {}) {
  const [totalTasks, setTotalTasks] = useState(null);
  const [dismissTick, setDismissTick] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    if (!anyAnnouncementNeedsTaskCount()) return;

    let cancelled = false;
    cloudApi.getAutomations({ limit: 1, offset: 0 })
      .then((result) => {
        if (cancelled) return;
        const count = result?.summary?.total_tasks ?? result?.pagination?.total ?? 0;
        setTotalTasks(count);
      })
      .catch(() => { /* ignore — announcements are non-critical */ });

    return () => { cancelled = true; };
  }, [enabled]);

  const dismiss = useCallback((id) => {
    dismissAnnouncement(id);
    setDismissTick((n) => n + 1); // re-resolve so the dismissed one drops out
  }, []);

  if (!enabled) return { announcements: [], dismiss };

  // If a task-count-gated announcement exists but the count hasn't loaded yet,
  // withhold it until we know — avoids a flash for users below the threshold.
  const ctx = { totalTasks: totalTasks ?? 0, plan, view };
  const announcements = totalTasks === null && anyAnnouncementNeedsTaskCount()
    ? getActiveAnnouncements(ctx).filter((a) => typeof a.targeting?.minTasks !== 'number')
    : getActiveAnnouncements(ctx);

  // `dismissTick` is referenced so the memo of this render re-runs after a dismiss.
  void dismissTick;

  return { announcements, dismiss };
}
